import React, { useState } from "react";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { InputAdornment, TextField } from "@mui/material";
import StackedImageSlider from "@/shared/components/common/slider";
import CardBoxLayout from "@/modules/user/layout/cardBoxLayout";
import { formatCardNumber, getBankInfo } from "./data.card.bank";
import { UserBankAccountsDaum } from "@/types/api/user/home.type.data";
import { useMutation } from "@tanstack/react-query";
import { postRequestCreateUserBankAccount } from "@/api/user/home";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import {toEnglishNumber,toPersianNumber,} from "@/hooks/useThousandsSeparator";
/**
 * ContentAddShetabCard
 *
 * نمایش و افزودن کارت شتاب کاربر.
 *
 * ویژگی‌ها:
 * - دریافت و نمایش کارت‌های بانکی کاربر با استفاده از StackedImageSlider
 * - امکان وارد کردن شماره کارت و شماره شبا با قالب‌بندی مناسب
 * - نمایش پیش‌نمایش کارت بر اساس شماره کارت وارد شده
 * - تعیین خودکار بانک و تصویر کارت با توجه به شماره کارت
 * - ارسال درخواست ایجاد کارت بانکی جدید با استفاده از mutation
 * - نمایش پیام موفقیت یا خطا با toast
 *
 * @param dataUserBankAccounts - آرایه‌ای از کارت‌های بانکی کاربر
 * @returns JSX.Element
 */

const ContentAddShetabCard = ({dataUserBankAccounts,}: {dataUserBankAccounts: UserBankAccountsDaum[] | undefined;}) => {
  const [cardNumber, setCardNumber] = useState("");
  const [bankCardImage, setBankCardImage] = useState<string | null>(null);
  const [cardNumberSheba, setCardNumberSheba] = useState("");
  const [titleBank, setTitleBank] = useState("");

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\D/g, "").slice(0, 16);
    setCardNumber(formatCardNumber(input));
    const bankInfo = getBankInfo(input);
    if (bankInfo) {
      setTitleBank(bankInfo?.name);
      setBankCardImage(`/bank-cards/${bankInfo.card}`);
    } else {
      setBankCardImage(null);
    }
  };

  const bankAccounts = dataUserBankAccounts ?? [];
  const itemsImg = bankAccounts.map((item) => {
    const bankInfo = getBankInfo(item.card_number);
    const imageFileName = bankInfo?.card || "default.jpg";
    return {
      id: item.id,
      url: `/bank-cards/${imageFileName}`,
      title: item.card_number,
      footer: item.user,
    };
  });

  const handleCardNumberSheba = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumberSheba(e.target.value);
  };

  const {
    mutate: fetchRequestCreateUserBankAccount,
    isPending: isPendingCreateUserBankAccount,
  } = useMutation({
    mutationFn: postRequestCreateUserBankAccount,
    onSuccess: (data: any) => {
      toast.success("کارت جدید با موفقیت اضافه شد");
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const handleSubmit = () => {
    const requestBody = {
      card_number: cardNumber,
      iban: cardNumberSheba,
      title: titleBank || "test",
    };

    fetchRequestCreateUserBankAccount(requestBody);
  };

  return (
    <StackKit direction={{ xs: "column-reverse", md: "row" }} className="w-full items-center justify-between gap-4">
      {/* Left Card Input */}
      <CardBoxLayout sx={{ width: { xs: "100%", md: "60%" },diplay:'flex',flexDirection:'column',gap:2, p: 3 }}>
        {/* Card Title */}
        <BoxKit className="w-full flex justify-start mb-3">
          <TypoGraphyKit variant="h6">اطلاعات کارت بانکی</TypoGraphyKit>
        </BoxKit>

        {/* Card Image Preview */}
        <StackKit className="w-full items-center justify-center mb-4">
          <BoxKit className="w-full max-w-[350px] h-[185px] border relative flex justify-center items-center bg-[#1a1a1a] rounded" sx={{borderImageSource:"linear-gradient(116.58deg, #1F1F1F -21.78%, #3D3D3D 184.44%)",}}>
            {bankCardImage ? (
              <StackKit className="rounded overflow-hidden h-full w-full">
                <img src={bankCardImage} alt="bank-card" className="w-full h-full object-contain"/>
                <BoxKit className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white font-bold text-[16px] sm:text-[18px]" sx={{ letterSpacing: "2px", direction: "ltr" }}>
                  {cardNumber}
                </BoxKit>
              </StackKit>
            ) : (
              <img src="/logoincontent.png" alt="logo" className="w-32 h-auto mx-auto" />
            )}
          </BoxKit>
        </StackKit>

        {/* Card Number Input */}
        <StackKit direction={{ xs: "column",sm: "column", md: "row" }} sx={{ width: "100%" }} justifyContent="space-between" alignItems="center" gap={2} mb={2}>
          <BoxKit className="w-full sm:w-auto">
            <TypoGraphyKit variant="body1">
              شماره کارت بانکی خود را وارد نمایید
            </TypoGraphyKit>
          </BoxKit>
          <TextField sx={{ width: { xs: "100%", sm: "50%" }, direction: "ltr" }} value={cardNumber ? toPersianNumber(cardNumber) : ''}
            placeholder={'شماره کارت'}
            onChange={(e) => {
              const englishNumber = toEnglishNumber(e.target.value);
              const rawValue = englishNumber.replace(/[^0-9]/g, "");
              if (rawValue.length <= 16) {
                setCardNumber(rawValue);
              }
            }}
          />
        </StackKit>

        {/* Sheba Number Input */}
        <StackKit direction={{ xs: "column",sm: "column", md: "row" }} sx={{ width: "100%" }} justifyContent="space-between" alignItems="center" gap={2} mb={2}>
          <BoxKit className="w-full sm:w-auto">
            <TypoGraphyKit variant="body1">
              شماره شبا کارت بانکی را وارد نمایید
            </TypoGraphyKit>
          </BoxKit>
          <TextField
              placeholder={'شماره شبا'}
              sx={{ width: { xs: "100%", sm: "50%" }, direction: "ltr" }}
              value={cardNumberSheba ? toPersianNumber(cardNumberSheba) : ""}
              onChange={(e) => {
                const englishNumber = toEnglishNumber(e.target.value);
                const rawValue = englishNumber.replace(/[^0-9]/g, "");
                if (rawValue.length <= 24) {
                  setCardNumberSheba(rawValue);
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">IR</InputAdornment>,
              }}
          />
        </StackKit>

        {/* Submit Button */}
        <StackKit direction="row" justifyContent={{ xs: "center", sm: "flex-end" }} mt={2}>
          <CustomButton sx={{ width: { xs: "100%", sm: "fit-content" }, height: "48px" }} onClick={handleSubmit}>
            تایید و ادامه
          </CustomButton>
        </StackKit>
      </CardBoxLayout>

      {/* Right Side: User Bank Cards or Empty */}
      {dataUserBankAccounts &&
        dataUserBankAccounts.length > 0 &&
        !dataUserBankAccounts.every((item) => item.status === "pending") ? (
        <StackKit direction="column" sx={{ width: { xs: "100%", md: "35%" } }} alignItems="center" gap={2}>
          <StackedImageSlider items={itemsImg}
            renderItem={(item) => (
              <div className="relative w-full h-full">
                <img src={item.url} alt="کارت" className="w-full h-full object-cover rounded"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/bank-cards/default.jpg";
                  }}
                />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/50 text-white py-2 px-3 text-sm">
                  {item.title}
                </div>
                <div className="absolute top-2 left-2 bg-white/50 text-[#333] py-1 px-2 text-xs">
                  {item.footer}
                </div>
              </div>
            )}
          />
        </StackKit>
      ) : (
        <StackKit className="bg-[#2E2E2E] w-full max-w-[283px] h-[160px] rounded" justifyContent="center" alignItems="center" mt={{ xs: 4, md: 0 }}>
          <TypoGraphyKit variant="body1">کارت بانکی وجود ندارد</TypoGraphyKit>
        </StackKit>
      )}
    </StackKit>

  );
};

export default ContentAddShetabCard;
